import {
  Button,
  Input,
  Search,
  UserCheck,
  formatPhoneNumberForDisplay,
} from "@ews-admin/global-design-system";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { ConfirmationModal } from "../../components/molecules/ConfirmationModal/ConfirmationModal";
import { useAppDispatch, useAppSelector } from "../../store/hooks";
import {
  clearUnverifiedUsersError,
  fetchUnverifiedUsersRequest,
  verifyUserRequest,
  selectUnverifiedUsers,
  selectUnverifiedUsersIsDataStale,
  selectUnverifiedUsersLoading,
} from "../../store/reducers/unverifiedUsersSlice";
import { UnverifiedUser } from "@/types/unverified-user.definition";
import { roleLabel } from "../../utils/enum-label-utils";

export const UnverifiedUsersManagement: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();

  const users = useAppSelector(selectUnverifiedUsers);
  const loading = useAppSelector(selectUnverifiedUsersLoading);
  const isDataStale = useAppSelector(selectUnverifiedUsersIsDataStale);

  const [searchTerm, setSearchTerm] = useState("");
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [selectedUser, setSelectedUser] = useState<UnverifiedUser | null>(null);

  const filteredUsers = users.filter(user => {
    const fullName =
      `${user.firstName ?? ""} ${user.lastName ?? ""}`.toLowerCase();
    const term = searchTerm.toLowerCase();
    return (
      fullName.includes(term) ||
      (user.email ?? "").toLowerCase().includes(term) ||
      (user.phoneNumber ?? "").toLowerCase().includes(term)
    );
  });

  useEffect(() => {
    if (users.length === 0 || isDataStale) {
      dispatch(fetchUnverifiedUsersRequest());
    }
  }, [dispatch, users.length, isDataStale]);

  useEffect(() => {
    return () => {
      dispatch(clearUnverifiedUsersError());
    };
  }, [dispatch]);

  const handleVerifyClick = (user: UnverifiedUser) => {
    setSelectedUser(user);
    setShowConfirmModal(true);
  };

  const handleConfirmVerify = () => {
    if (!selectedUser) return;
    dispatch(verifyUserRequest({ userId: selectedUser.id }));
    setShowConfirmModal(false);
    setSelectedUser(null);
  };

  const handleCancelVerify = () => {
    setShowConfirmModal(false);
    setSelectedUser(null);
  };

  const getDisplayName = (user: UnverifiedUser) => {
    const name = `${user.firstName ?? ""} ${user.lastName ?? ""}`.trim();
    return name || user.email || t("common.unknown");
  };

  return (
    <div className="page-container">
      <div className="flex justify-between items-center page-header">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">
            {t("admin.unverifiedUsers.title")}
          </h2>
          <p className="text-gray-600">{t("admin.unverifiedUsers.subtitle")}</p>
        </div>
        <Button
          variant="outline"
          onClick={() => dispatch(fetchUnverifiedUsersRequest())}
          disabled={loading}
        >
          {t("common.refresh")}
        </Button>
      </div>

      <div className="flex flex-col gap-4 page-section sm:flex-row">
        <div className="flex-1">
          <Input
            placeholder={t("admin.unverifiedUsers.searchPlaceholder")}
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
            leftIcon={<Search className="w-4 h-4" />}
          />
        </div>
      </div>

      <div className="page-section">
        {loading ? (
          <div className="py-12 text-center">
            <p className="text-sm text-neutral-500">{t("common.loading")}</p>
          </div>
        ) : filteredUsers.length === 0 ? (
          <div className="py-12 text-center">
            <UserCheck className="mx-auto w-12 h-12 text-neutral-400" />
            <h3 className="mt-2 text-sm font-medium text-neutral-900">
              {t("admin.unverifiedUsers.noUsers")}
            </h3>
            <p className="mt-1 text-sm text-neutral-500">
              {t("admin.unverifiedUsers.noUsersDescription")}
            </p>
          </div>
        ) : (
          <div className="overflow-hidden rounded-lg border border-neutral-200 bg-white">
            <table className="min-w-full divide-y divide-neutral-200">
              <thead className="bg-neutral-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.unverifiedUsers.table.name")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.unverifiedUsers.table.email")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.unverifiedUsers.table.phone")}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("admin.unverifiedUsers.table.role")}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-neutral-500 uppercase tracking-wider">
                    {t("common.actions")}
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-200 bg-white">
                {filteredUsers.map(user => (
                  <tr key={user.id} className="hover:bg-neutral-50">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-3">
                        <div className="flex-shrink-0 w-8 h-8 rounded-full bg-neutral-100 flex items-center justify-center">
                          <span className="text-xs font-medium text-neutral-600">
                            {(
                              user.firstName?.[0] ??
                              user.email?.[0] ??
                              "?"
                            ).toUpperCase()}
                          </span>
                        </div>
                        <span className="text-sm font-medium text-neutral-900">
                          {getDisplayName(user)}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {user.email ?? "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-neutral-600">
                      {user.phoneNumber
                        ? formatPhoneNumberForDisplay(user.phoneNumber)
                        : "—"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-neutral-100 text-neutral-800">
                        {/* MED-1543: printed the raw enum ("PATIENT"); the
                            users list has shown the label since MED-1462. */}
                        {user.roleName ? roleLabel(t, user.roleName) : "—"}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right">
                      <Button
                        variant="outline"
                        onClick={() => handleVerifyClick(user)}
                        className="text-info-600 border-info-300 hover:bg-info-50"
                      >
                        {t("admin.unverifiedUsers.verify")}
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <ConfirmationModal
        isOpen={showConfirmModal}
        onClose={handleCancelVerify}
        onConfirm={handleConfirmVerify}
        title={t("admin.unverifiedUsers.verifyTitle")}
        message={t("admin.unverifiedUsers.verifyMessage", {
          name: selectedUser ? getDisplayName(selectedUser) : "",
        })}
        confirmLabel={t("admin.unverifiedUsers.verify")}
        cancelLabel={t("common.cancel")}
        variant="info"
        isLoading={loading}
      />
    </div>
  );
};
